import { Button, Spin } from "antd";
import CheckboxInput from "components/Form/CheckboxInput";
import LabeledInput from "components/Form/LabeledInput";
import LabeledUploadInput from "components/Form/LabeledUploadInput";
import WarningIcon from "components/Icons/WarningIcon";
import useEditFleetModel from "hooks/fleetModels/useEditFleetModel";

export default function EditFleetModel() {
  const { control, handleSubmit, loading, onSubmit, handleUpload } =
    useEditFleetModel();

  return (
    <div
      className="w-full bg-white rounded-lg p-6"
      style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
    >
      <div className="w-full pt-5 pb-5">
        <div className="w-full">
          <h1 className="p-5 font-light text-lg text-center">
            Edit Fleet Model
          </h1>
          <div className="w-full">
            <div className="w-full relative h-1 top-7 overflow-clip rounded-md">
              <div className="w-1/2 bg-black h-full rounded-md"></div>
            </div>
            <div className="relative w-full flex justify-center">
              <div className="relative h-14 w-14 bg-black rounded-full flex items-center justify-center text-white text-2xl">
                <WarningIcon />
              </div>
            </div>
          </div>
          <p className="text-center mt-2 text-sm font-normal">
            Model Information
          </p>
        </div>
        <div className="w-full py-8 px-5">
          {loading.page ? (
            <div className="w-full flex items-center justify-center py-20">
              <Spin />
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="w-full flex flex-wrap"
            >
              <div className="w-full px-4 pt-5 md:w-1/2">
                <LabeledInput
                  label="Model Name"
                  type="text"
                  control={control}
                  name="name"
                  placeholder="Model Name"
                />
              </div>
              <div className="w-full px-4 pt-5 md:w-1/2">
                <LabeledInput
                  label="Seat Number"
                  type="number"
                  control={control}
                  name="seat_number"
                  placeholder="Seat Number"
                />
              </div>
              <div className="w-full px-4 pt-5 md:w-1/2">
                <LabeledInput
                  label="Base Fare"
                  type="number"
                  control={control}
                  name="base_fare"
                  placeholder="Base Fare"
                />
              </div>
              <div className="w-full px-4 pt-5 md:w-1/2">
                <LabeledInput
                  label="Price Per Kilo Meter"
                  type="number"
                  control={control}
                  name="price_per_kilo_meter"
                  placeholder="Price Per Kilo Meter"
                />
              </div>
              <div className="w-full px-4 pt-5 md:w-1/2">
                <LabeledInput
                  label="Price Per Minute"
                  type="number"
                  control={control}
                  name="price_per_minute"
                  placeholder="Price Per Minute"
                />
              </div>
              <div className="w-full px-4 pt-5 md:w-1/3">
                <LabeledInput
                  label="Special Base Fare"
                  type="number"
                  control={control}
                  name="special_base_fare"
                  placeholder="Special Base Fare"
                />
              </div>
              <div className="w-full px-4 pt-5 md:w-1/3">
                <LabeledInput
                  label="Special Price Per Kilo Meter"
                  type="number"
                  control={control}
                  name="special_price_per_kilo_meter"
                  placeholder="Special Price Per Kilo Meter"
                />
              </div>
              <div className="w-full px-4 pt-5 md:w-1/3">
                <LabeledInput
                  label="Special Price Per Minute"
                  type="number"
                  control={control}
                  name="special_price_per_minute"
                  placeholder="Special Price Per Minute"
                />
              </div>
              <div className="w-full px-4 pt-5 md:w-1/3">
                <LabeledInput
                  label="Search Radius (meters)"
                  type="number"
                  step="100"
                  control={control}
                  name="search_radius"
                  placeholder="Leave empty to use default"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Custom search radius in meters for this fleet model. If left empty, the system will use the default search radius from configuration.
                  <br />
                  <strong>Examples:</strong> Sedan: 1000m (1km), SUV: 3000m (3km)
                </p>
              </div>
              <div className="w-full px-4 pt-10 md:w-1/3 flex items-center">
                <CheckboxInput
                  title="Is Shared"
                  control={control}
                  name="is_shared"
                />
              </div>
              <div className="w-full">
                <p className="text-xl font-bold mt-6">Exceptional Fares</p>
                <div className="w-full pb-3 flex flex-wrap">
                  <div className="w-full px-4 pt-5 md:w-1/3">
                    <LabeledInput
                      label="ExceptionalKilo Meter"
                      type="number"
                      control={control}
                      name="exceptional_km"
                      placeholder="Exceptional Kilo Meter"
                    />
                  </div>
                  <div className="w-full px-4 pt-5 md:w-1/3">
                    <LabeledInput
                      label="Exceptional Base Fare"
                      type="number"
                      control={control}
                      name="exceptional_base_fare"
                      placeholder="Exceptional Base Fare"
                    />
                  </div>
                  <div className="w-full px-4 pt-5 md:w-1/3">
                    <LabeledInput
                      label="Exceptional Price Per Kilo Meter"
                      type="number"
                      control={control}
                      name="exceptional_price_per_kilo_meter"
                      placeholder="Exceptional Price Per Kilo Meter"
                    />
                  </div>
                  <div className="w-full px-4 pt-5 md:w-1/3">
                    <LabeledInput
                      label="Exceptional Price Per Minute"
                      type="number"
                      control={control}
                      name="exceptional_price_per_minute"
                      placeholder="Exceptional Price Per Minute"
                    />
                  </div>
                </div>
              </div>
              <div className="w-full">
                <p className="text-xl font-bold mt-6">Dispatcher Pricing Modifiers</p>
                <div className="w-full pb-3 flex flex-wrap">
                  <div className="w-full px-4 pt-5 md:w-1/4">
                    <LabeledInput
                      label="Manual Trip Base Fare"
                      type="number"
                      control={control}
                      name="manual_base_fare"
                      placeholder="Manual Trip Base Fare"
                    />
                  </div>
                  <div className="w-full px-4 pt-5 md:w-1/4">
                    <LabeledInput
                      label="App Booking Margin (%)"
                      type="number"
                      control={control}
                      name="app_booking_percent"
                      placeholder="App Booking Margin (%)"
                    />
                  </div>
                  <div className="w-full px-4 pt-5 md:w-1/4">
                    <LabeledInput
                      label="Admin Dispatch Margin (%)"
                      type="number"
                      control={control}
                      name="admin_dispatch_percent"
                      placeholder="Admin Dispatch Margin (%)"
                    />
                  </div>
                  <div className="w-full px-4 pt-5 md:w-1/4">
                    <LabeledInput
                      label="Passenger Trip Reward (%)"
                      type="number"
                      control={control}
                      name="passenger_reward_percent"
                      placeholder="Passenger Trip Reward (%)"
                    />
                  </div>
                </div>
              </div>
              <div className="w-full px-4 pt-5 md:w-1/3">
                <LabeledUploadInput
                  control={control}
                  name="image"
                  placeholder="Select Fleet Image"
                  label="Fleet Image"
                  action={handleUpload}
                />
              </div>
              <div className="w-full flex mt-8 justify-end">
                <Button
                  loading={loading.button}
                  htmlType="submit"
                  type="primary"
                  className="py-2 px-8 rounded-md float-right h-auto font-medium w-fit shadow-none"
                >
                  Update Model
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
